import {Texture, Application, Sprite, Text} from 'pixi.js';

const degreeToRadians = degree => degree * (Math.PI / 180);

class ImageViewer
{
    constructor ({element, size}) {
        this.container = element.querySelector('.image-container');
        this.slider = element.querySelector('input[type=range]');
        this.rotateBtn = element.querySelector('.rotate');
        this.resetBtn = element.querySelector('.reset');
        this.src = element.dataset.src;
        
        this.size = size;
    }

    async load () {
        this.createApp();
        
        try {
            await this.loadSource();    
        } catch (e) {
            return this.writeErrorText();
        }

        this.createImage();
        this.initDragAndDrop();
        this.initZoom();
        this.initRotation();
        this.initResetting();
    }
    
    writeErrorText () {
        const error = new Text('Image loading error.');
        
        error.x = this.app.renderer.width * 0.4;
        error.y = this.app.renderer.height * 0.5;

        this.app.stage.addChild(error);
    }
    
    async loadSource () {
        this.source = await Texture.fromURL(`${this.src}?no-cache-${Math.random().toString(32)}`, {
            resourceOptions: {
                crossorigin: false
            }
        });
        
        this.defaultScale = +(Math.min(this.app.renderer.height / this.source.height, this.app.renderer.width / this.source.width) * 0.95).toFixed(2);
    }
    
    createApp () {
        this.app = new Application({
            background: '#f4f7fa',
            width: this.size.width,
            height: this.size.height,
        });

        this.app.renderer.events.features.wheel = true;
        this.container.append(this.app.view);
    }
    
    pointer () {
        const {x, y} = this.app.renderer.events.pointer.global;
        
        return {x, y};
    }
    
    initZoom () {
        this.app.renderer.view.addEventListener('wheel', e => e.preventDefault(), {passive: false});
        this.app.renderer.view.addEventListener('mousewheel', e => e.preventDefault(), {passive: false})

        const step = +((this.defaultScale * 10) / 100).toFixed(2);
        
        const zoomSize = () => +this.slider.value;
        const applySliderValue = () => this.image.scale.set(this.defaultScale + zoomSize() * step);

        this.slider.oninput = () => applySliderValue();
        this.image.onwheel = (e) => {
            this.slider.value = zoomSize() + (e.deltaY > 0 ? -1 : 1);
            
            applySliderValue();
        };
    }

    initDragAndDrop () {
        let dragStartPosition = null;

        const onDragStart = () => dragStartPosition = this.pointer();
        const onDragEnd = () => dragStartPosition = null;
        const onDragMove = () => {
            if (!dragStartPosition) {
                return;
            }

            this.image.position.x += this.pointer().x - dragStartPosition.x;
            this.image.position.y += this.pointer().y - dragStartPosition.y;

            dragStartPosition = this.pointer();
        }

        this.image
            .on('mouseover', () => document.body.style.cursor = 'move')
            .on('mouseout', () => document.body.style.cursor = 'default')
            .on('mousedown', onDragStart)
            .on('touchstart', onDragStart)
            .on('mouseup', onDragEnd)
            .on('mouseupoutside', onDragEnd)
            .on('touchend', onDragEnd)
            .on('touchendoutside', onDragEnd)
            .on('mousemove', onDragMove)
            .on('touchmove', onDragMove);
    }

    initRotation () {
        this.rotateBtn.onclick = () => {
            this.image.rotation += degreeToRadians(90);

            if (this.image.rotation === degreeToRadians(360)) {
                this.image.rotation = 0;
            }
        };
    }
    
    initResetting () {
        this.resetBtn.onclick = () => {
            this.image.rotation = 0;
            this.image.scale.set(this.defaultScale);
            this.image.position.set(this.app.renderer.width / 2, this.app.renderer.height / 2);
            this.slider.value = 0;
        };
    }
    
    createImage () {
        this.image = new Sprite(this.source);
        this.image.eventMode = 'dynamic';
        this.image.position.set(this.app.renderer.width / 2, this.app.renderer.height / 2);
        this.image.anchor.set(.5)
        this.image.scale.set(this.defaultScale);

        this.app.stage.addChild(this.image);
    }
}

export { ImageViewer };